import { styleText } from 'node:util'

import * as p from '@clack/prompts'
import type { BetterstartIntegrationId } from '@integration-engine/types.js'
import {
  type CollectedIntegrationConfig,
  collectIntegrationConfig
} from '@next-adapter/integration-runtime.js'
import type { BetterstartPluginId } from '@plugin-engine/types.js'

export type StorageMode = 'local' | 'r2'
type EmailProvider = 'resend' | 'skip' | 'cloudflare'

export interface PluginPromptResult {
  plugins: BetterstartPluginId[]
  integrations: BetterstartIntegrationId[]
  storage: StorageMode
  integrationConfig: CollectedIntegrationConfig
}

export async function promptPlugins(
  cwd: string,
  options?: {
    /**
     * Resend API key already provisioned via the Vercel marketplace. When set,
     * the email provider prompt is skipped: Resend is auto-selected and only
     * the from address is collected.
     */
    vercelResendApiKey?: string
  }
): Promise<PluginPromptResult> {
  const sections: CollectedIntegrationConfig['sections'] = []
  const overwriteKeys = new Set<string>()
  const mergeIntegrationConfig = (collected: CollectedIntegrationConfig): void => {
    sections.push(...collected.sections)
    for (const key of collected.overwriteKeys) {
      overwriteKeys.add(key)
    }
  }

  const selectedPlugins = await p.multiselect({
    message: `Select presets\n${styleText('dim', 'Press [Spacebar] to select/unselect')}`,
    options: [
      {
        value: 'blog' as const,
        label: 'Blog',
        hint: 'Posts'
      }
    ],
    required: false,
    initialValues: ['blog' as const]
  })

  if (p.isCancel(selectedPlugins)) {
    p.cancel('Setup cancelled.')
    process.exit(0)
  }

  const storage = await p.select({
    message: 'Choose a file storage mode',
    options: [
      {
        value: 'r2' as const,
        label: 'Cloudflare R2',
        hint: 'recommended for hosted/serverless production'
      },
      {
        value: 'local' as const,
        label: 'Local filesystem (public/media)',
        hint: 'development or self-hosted persistent disk'
      }
    ],
    initialValue: 'r2' as const
  })

  if (p.isCancel(storage)) {
    p.cancel('Setup cancelled.')
    process.exit(0)
  }

  // Collect R2 credentials immediately after the storage choice so input is
  // gathered up front, right where the user made the decision.
  if (storage === 'r2') {
    mergeIntegrationConfig(await collectIntegrationConfig(cwd, ['r2']))
  }

  const integrations: BetterstartIntegrationId[] = []

  if (options?.vercelResendApiKey) {
    // Resend was already provisioned via the Vercel marketplace — no provider
    // question; only the from address still needs collecting.
    p.log.info(`Email provider: Resend ${styleText('dim', '(connected via Vercel)')}`)
    integrations.push('resend')
    mergeIntegrationConfig(
      await collectIntegrationConfig(cwd, ['resend'], {
        resendApiKey: options.vercelResendApiKey
      })
    )
  } else {
    const selectedEmailProvider = await p.select<EmailProvider>({
      message: `Which email provider do you want to set up?\n${styleText(
        'dim',
        'Auth + Forms email delivery'
      )}`,
      options: [
        {
          value: 'resend' as const,
          label: 'Resend'
        },
        {
          value: 'skip' as const,
          label: 'Skip'
        },
        {
          value: 'cloudflare' as const,
          label: `Cloudflare ${styleText('dim', '(coming soon)')}`,
          disabled: true
        }
      ],
      initialValue: 'resend' as const
    })

    if (p.isCancel(selectedEmailProvider)) {
      p.cancel('Setup cancelled.')
      process.exit(0)
    }

    // Collect Resend credentials immediately after the email choice.
    if (selectedEmailProvider === 'resend') {
      integrations.push('resend')
      mergeIntegrationConfig(await collectIntegrationConfig(cwd, ['resend']))
    }
  }

  if (storage === 'r2') {
    integrations.push('r2')
  }

  return {
    plugins: selectedPlugins,
    integrations,
    storage,
    integrationConfig: { sections, overwriteKeys }
  }
}
